export const LIVE_STATUS_WINDOW_MS = 30000;

export function deviceLiveStatus(dateTime, now = Date.now(), windowMs = LIVE_STATUS_WINDOW_MS) {
  if (!dateTime) return "inactive";
  const ts = new Date(dateTime).getTime();
  if (Number.isNaN(ts)) return "inactive";
  return Math.abs(now - ts) < windowMs ? "active" : "inactive";
}

export function buildWsUrl(baseUrl, { token, isAdmin } = {}) {
  const override = process.env.REACT_APP_WS_URL;
  let url = override;
  if (!url) {
    const httpBase = (baseUrl || process.env.REACT_APP_BASE_URL || "").replace(/\/$/, "");
    url = `${httpBase.replace(/^http/i, (match) =>
      match.toLowerCase() === "https" ? "wss" : "ws"
    )}/ws`;
  }
  const params = new URLSearchParams();
  if (token) params.set("token", token);
  if (isAdmin) params.set("role", "admin");
  const query = params.toString();
  return query ? `${url}?${query}` : url;
}

export function getAuthForSocket() {
  const token = localStorage.getItem("accessToken");
  const isAdmin = Boolean(localStorage.getItem("adminLogin"));
  return { token, isAdmin };
}

function notify(listeners, message) {
  listeners.forEach((listener) => {
    try {
      listener(message);
    } catch (error) {
      console.error("Live socket listener error:", error);
    }
  });
}

export function createLiveSocket({
  WebSocketImpl = typeof WebSocket === "undefined" ? null : WebSocket,
  getAuth = getAuthForSocket,
  getBaseUrl = () => process.env.REACT_APP_BASE_URL,
  reconnectMs = 1000,
  maxReconnectMs = 15000,
} = {}) {
  let ws = null;
  let wantedOpen = false;
  let reconnectTimer = null;
  let pingTimer = null;
  let subscribeTimer = null;
  let backoff = reconnectMs;
  const listeners = new Set();
  const deviceRefCount = new Map();
  const subscribeDebounceMs = 50;

  const currentIds = () => Array.from(deviceRefCount.keys());

  const send = (payload) => {
    if (!ws || ws.readyState !== 1) return;
    ws.send(JSON.stringify(payload));
  };

  const sendSubscribe = () => {
    send({ type: "subscribe", deviceIds: currentIds() });
  };

  const sendSubscribeSoon = () => {
    if (subscribeTimer) clearTimeout(subscribeTimer);
    subscribeTimer = setTimeout(() => {
      subscribeTimer = null;
      sendSubscribe();
    }, subscribeDebounceMs);
  };

  const stopTimers = () => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    if (pingTimer) {
      clearInterval(pingTimer);
      pingTimer = null;
    }
    if (subscribeTimer) {
      clearTimeout(subscribeTimer);
      subscribeTimer = null;
    }
  };

  const scheduleReconnect = () => {
    if (!wantedOpen || reconnectTimer) return;
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      connect();
    }, backoff);
    backoff = Math.min(backoff * 2, maxReconnectMs);
  };

  const handleMessage = (event) => {
    let message;
    try {
      message = JSON.parse(event.data);
    } catch (error) {
      return;
    }
    notify(listeners, message);
  };

  const connect = () => {
    if (!wantedOpen || !WebSocketImpl) return;
    if (ws && (ws.readyState === 0 || ws.readyState === 1)) return;

    const url = buildWsUrl(getBaseUrl(), getAuth());
    ws = new WebSocketImpl(url);
    notify(listeners, { type: "connection", connected: false });

    ws.onopen = () => {
      backoff = reconnectMs;
      if (subscribeTimer) {
        clearTimeout(subscribeTimer);
        subscribeTimer = null;
      }
      notify(listeners, { type: "connection", connected: true });
      sendSubscribe();
      pingTimer = setInterval(() => send({ type: "ping" }), 25000);
    };

    ws.onmessage = handleMessage;

    ws.onclose = () => {
      ws = null;
      if (pingTimer) {
        clearInterval(pingTimer);
        pingTimer = null;
      }
      notify(listeners, { type: "connection", connected: false });
      scheduleReconnect();
    };

    ws.onerror = () => {
      if (ws) ws.close();
    };
  };

  const close = () => {
    stopTimers();
    if (ws) {
      const socket = ws;
      ws = null;
      socket.onclose = null;
      socket.close();
    }
    notify(listeners, { type: "connection", connected: false });
  };

  return {
    subscribe(deviceIds, listener) {
      wantedOpen = true;
      const ids = (deviceIds || [])
        .map((id) => Number(id))
        .filter((id) => !Number.isNaN(id));

      ids.forEach((id) => {
        deviceRefCount.set(id, (deviceRefCount.get(id) || 0) + 1);
      });
      listeners.add(listener);
      connect();
      sendSubscribeSoon();

      return () => {
        listeners.delete(listener);
        ids.forEach((id) => {
          const next = (deviceRefCount.get(id) || 1) - 1;
          if (next <= 0) deviceRefCount.delete(id);
          else deviceRefCount.set(id, next);
        });
        sendSubscribe();
        if (deviceRefCount.size === 0 && listeners.size === 0) {
          wantedOpen = false;
          close();
        }
      };
    },
    getSubscribedIds: currentIds,
    isOpen: () => Boolean(ws && ws.readyState === 1),
    close,
    _debug: {
      sendSubscribe,
      handleMessage,
    },
  };
}

let sharedSocket = null;

export function getLiveSocket() {
  if (!sharedSocket) {
    sharedSocket = createLiveSocket();
  }
  return sharedSocket;
}

export function resetLiveSocketForTests() {
  if (sharedSocket) sharedSocket.close();
  sharedSocket = null;
}
